import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Card,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { LineChart } from '@mui/x-charts';
import {
  AccessTimeOutlined as HourlyPlanIcon,
  ArrowRight as LinkIcon,
  FileDownloadOutlined as DownloadIcon,
} from '@mui/icons-material';

import { useSession, useMisc } from 'store';
import { allTrackers, postbackTypes, jobAttrs, jobAttrExcludeRegion, jobAttrFixedOverrun } from 'consts';
import { callApi, downloadHourlyPlanCsv, downloadTrafficCurveCsv, formatPercent, getTrafficCurvePercents } from 'utils';
import { Popup, toast } from 'components';
import { t } from 'i18n';

const StatCard = ({name, value}) => (
  <Card
    sx={{
      padding: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
  >
    <Box>
      <Typography variant="caption" component="div" gutterBottom sx={{mt: '-3px'}}>{name}</Typography>
      <Typography variant="h6" sx={{whiteSpace: 'nowrap'}}>
        {value === undefined ? 'N/A' : value}
      </Typography>
    </Box>
  </Card>
);

const DayCard = ({day, imps, dayparts, isPast}) => (
  <Card
    sx={{
      padding: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme => isPast
        ? theme.vars.palette.app.schedulePastBackground
        : theme.vars.palette.background.paper,
    }}
  >
    <Box>
      <Typography variant="caption" component="div" gutterBottom sx={{mt: '-3px', whiteSpace: 'nowrap'}}>
        {day}{dayparts && (<span style={{marginLeft: "8px"}}>{dayparts}</span>)}
      </Typography>
      <Typography variant="h6">
        {imps === undefined ? 'N/A' : imps}
      </Typography>
    </Box>
  </Card>
);

function getCumulativePageCount(state, startIndex) {
  const counts = Array.isArray(state.jump_cnt) ? state.jump_cnt : state.page_cnt;
  if (!Array.isArray(counts)) {
    return 0;
  }

  if (Array.isArray(state.jump_cnt)) {
    return Number(counts[startIndex]) || 0;
  }

  return counts.slice(startIndex).reduce((total, cnt) => total + (Number(cnt) || 0), 0);
}

const Label = ({name}) => (
  <Box sx={{color: 'text.secondary', textAlign: 'right', mr: 1}}>{t(name)}:</Box>
);

const TrafficCurveChart = ({data, onClose}) => {
  if (!Array.isArray(data)) {
    return null;
  }
  const ydata = getTrafficCurvePercents(data);
  return (
    <Popup
      open={true}
      onClose={onClose}
      title={t('Traffic Curve')}
      styles={{width: '650px'}}
    >
      <LineChart
        xAxis={[{ data: Array.from({ length: 24 }, (_, idx) => idx) }]}
        series={[ {data: ydata} ]}
        height={300}
      />
    </Popup>
  );
}

const indicators = [
  {
    name: t('Impressions'),
    valid: (_) => true,
    value: (s) => s.imps || 0,
  }, {
    name: t('Clicks'),
    valid: (_) => true,
    value: (s) => s.clicks || 0,
  }, {
    name: t('Landing Rate'),
    valid: (s) => s.clicks > 0,
    value: (s) => (100*s.landing/s.clicks).toFixed(2) + '%',
  }, {
    name: 'CTR',
    valid: (s) => s.clicks > 0,
    value: (s) => (100*s.clicks/s.imps).toFixed(2) + '%',
  }, {
    name: t('Stay'),
    valid: (s) => s.clicks > 0 && 100*s.landing > s.clicks, // landing rate > 1%
    value: (s) => Math.ceil(s.avg_stay) + 's',
  }, {
    name: t('Progress'),
    valid: (_) => true,
    value: (s) => s.progress?.[1] ? s.progress.map(p => (100*p).toFixed(1) + '%').join(' / ') : '--',
  }, {
    name: t('Adx Fill'),
    valid: (s) => s.adx_fill > 0,
    value: (s) => (100*s.adx_fill).toFixed(2) + '%',
  },
  ...['Jump2', 'Jump3'].map((name, i) => ({
    name: t(name),
    valid: (s) => s.landing > 0 && getCumulativePageCount(s, i) > 0,
    value: (s) => (100*getCumulativePageCount(s, i)/s.landing).toFixed(1) + '%',
  })),
  ...allTrackers.map((name, i) => ({
    name,
    valid: (s) => s.tracker_cnt[i] > 0,  // && 100*s.landing > s.clicks
    value: (s) => (100*s.tracker_cnt[i]/s.landing).toFixed(1) + '% / ' + Math.ceil(s.tracker_stay[i]) + 's',
  })),
  // bounce rate for trackers
  ...allTrackers.map((name, i) => ({
    name: name + ' B/R',
    valid: (s) => s.tracker_bounce[i] > 0 && s.tracker_cnt[i] > 0,
    value: (s) => (100*s.tracker_bounce[i]/s.tracker_cnt[i]).toFixed(1) + '%',
  })),
];

function formatOsvPct(pct) {
  return Object.keys(pct).sort().map(osv => {
    const os = ({i: 'iOS', a: 'Android'})[osv[0]] || 'Unknown';
    return os + osv.substring(1) + ': ' + pct[osv] + '%';
  }).join(', ');
}

function formatCustomizePercent(val) {
  if (val === undefined || val === null) {
    return '-';
  }
  const s = ('' + val).trim();
  if (s === '') {
    return '-';
  }
  const n = Number.parseFloat(s.replace('%', ''));
  if (!Number.isNaN(n) && n === 0) {
    return '-';
  }
  return s.endsWith('%') ? s : s + '%';
}

function formatUATpl(tpls) {
  const dat = {osv: '15', model: 'V2357A', build: 'UP1A.231005.007', brver: '120.0.6099.193'};
  const rep = (s) => Object.keys(dat).reduce((s, a) => s.replaceAll('__'+a+'__', dat[a]), s);
  return Object.entries(tpls).map(([os, ua]) => {
    os = ({ios: 'iOS', android: 'Android'})[os] || 'Unknown';
    return os + ': ' + rep(ua);
  }).join('\n');
}

const JobState = ({state, onClose}) => {
  const session = useSession((store) => store.session);
  const misc = useMisc((store) => store.data);
  const [ext, setExt] = useState({});
  const [trafficCurve, setTrafficCurve] = useState(null);
  const [trafficCurves, setTrafficCurves] = useState({});

  const [trafficChart, setTrafficChart] = useState(false);
  const closeTrafficChart = () => setTrafficChart(false);

  const {job, stats} = state;
  const isAdmin = session.role === 'admin';
  const trafficCurveName = trafficCurves[job.traffic_curve] || job.traffic_curve || '';
  const hasTrafficCurveData = Array.isArray(trafficCurve) && trafficCurve.length === 24;
  const openTrafficChart = () => hasTrafficCurveData && setTrafficChart(true);

  useEffect(() => {
    let active = true;
    if (!isAdmin) {
      setTrafficCurves(misc.traffic_curve || {});
      return () => { active = false; };
    }

    const org = Number(job.org);
    if (!(org > 0)) {
      setTrafficCurves(misc.traffic_curve || {});
      return () => { active = false; };
    }

    callApi(`/misc?org=${org}`)
      .then(res => active && setTrafficCurves(res?.traffic_curve || {}))
      .catch(err => {
        console.error(err);
        active && setTrafficCurves(misc.traffic_curve || {});
      });
    return () => { active = false; };
  }, [isAdmin, job.org, misc.traffic_curve]);

  useEffect(() => {
    let active = true;
    setExt({});
    setTrafficCurve(null);
    setTrafficChart(false);
    callApi(`/jobs/${job.id}/extra`).then(res => active && setExt(res)).catch(console.error);
    callApi(`/jobs/${job.id}/traffic_curve`).then(res => active && setTrafficCurve(res?.traffic_curve)).catch(console.error);
    return () => {
      active = false;
    };
  }, [job]);

  const downloadTrafficCurve = () => {
    if (!hasTrafficCurveData) {
      return;
    }
    downloadTrafficCurveCsv(trafficCurve, `traffic-curve-${job.id}-${trafficCurveName || job.traffic_curve}`);
  };

  const downloadHourlyPlan = () => {
    if (!hasTrafficCurveData) {
      return;
    }
    callApi(`/jobs/${job.id}/hourly_plan`).then(res => {
      if (!Array.isArray(res?.rows) || res.rows.length !== 24) {
        toast('没有可下载的今日小时数量', 'warning');
        return;
      }
      downloadHourlyPlanCsv(res, `hourly-plan-${job.id}-${trafficCurveName || job.traffic_curve}`);
    }).catch(console.error);
  };

  // build current running stats
  let runStats;
  if (stats) {
    runStats = indicators.filter(idc => idc.valid(stats)).map(idc => ({name: idc.name, value: idc.value(stats)}));
  }

  // build every day imps
  const startDate = dayjs(job.start_date), endDate = dayjs(job.end_date);
  const hasDayparts = Object.keys(job.dayparts).length > 0;
  const today = dayjs().format('YYYYMMDD');
  const schedule = [];
  for (let d = startDate; !d.isAfter(endDate, 'day'); d = d.add(1, 'day')) {
    const ymd = d.format('YYYYMMDD');
    schedule.push({
      day: d.format('MM-DD'),
      imps: isNaN(job.daily_imps[ymd]) ? job.daily_imps.default : job.daily_imps[ymd],
      dayparts: hasDayparts ? (job.dayparts[ymd] || job.dayparts.default || ['00:00','23:59']).join('~') : null,
      isPast: ymd < today,
    });
  }

  let devicePct = misc['device_filter'][job.device_filter];

  const cust = JSON.parse(job.customize || '{}');
  const jump2Plan = job.jump2;
  const bounceTarget = cust?.bounce;
  const displayOverrun = cust?.cross_day_schedule?.role === 'main'
    ? cust.cross_day_schedule.overflow_percent
    : job.overrun;
  if (cust?.device) {
    const s = cust.device.replace('_', ' ') + '%';
    devicePct = s.charAt(0).toUpperCase() + s.slice(1);
  }

  let attrs = jobAttrs.filter(a => a.val !== jobAttrExcludeRegion && (job.attrs&a.val) > 0).map(a => a.display).join(', ');

  return (
    <Popup
      open={Boolean(job)}
      onClose={onClose}
      title={`${job.id} - ${job.name}`}
    >
      {runStats && (
      <Grid sx={{mt: 0}} container spacing={2}>
        {runStats.map((stat, i) => (
        <Grid item lg={2} sm={4} xs={6} key={i}>
          <StatCard {...stat} />
        </Grid>
        ))}
      </Grid>
      )}

      <Typography variant="h6" sx={{mt: 2, pb: 1, borderBottomColor: 'divider', borderBottomStyle: 'solid', borderBottomWidth: 1}}>Job Schedule</Typography>
      <Grid sx={{mt: -1}} container spacing={2}>
        {schedule.map(day => (
        <Grid item lg={2} sm={4} xs={6} key={day.day}>
          <DayCard {...day} />
        </Grid>
        ))}
      </Grid>

      <Typography variant="h6" sx={{mt: 2, mb: 1, pb: 1, borderBottomColor: 'divider', borderBottomStyle: 'solid', borderBottomWidth: 1}}>Job Details</Typography>
      <Grid container spacing={1} wrap="wrap">
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Start/End Date"/></Grid>
          <Grid item xs>{job.start_date + ' ~ ' + job.end_date}</Grid>
        </Grid>
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Expect CTR"/></Grid>
          <Grid item xs>{job.ctr}%</Grid>
        </Grid>
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Target Jump2"/></Grid>
          <Grid item xs>{formatCustomizePercent(jump2Plan)}</Grid>
        </Grid>
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Bounce Target"/></Grid>
          <Grid item xs>{formatCustomizePercent(bounceTarget)}</Grid>
        </Grid>
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Overrun"/></Grid>
          <Grid item xs>
            {displayOverrun}%
            {(job.attrs&jobAttrFixedOverrun) > 0 && (
            <Typography variant="span" sx={{ml: 1.2, color: 'text.disabled'}}>fixed</Typography>
            )}
          </Grid>
        </Grid>
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Traffic Curve"/></Grid>
          <Grid item xs sx={{display: 'flex', alignItems: 'center'}}>
            <Typography
              variant="span"
              sx={hasTrafficCurveData && ({
                cursor: 'pointer',
                textDecorationLine: 'underline',
                textDecorationStyle: 'dashed',
              })}
              onClick={openTrafficChart}
            >{trafficCurveName}</Typography>
            {hasTrafficCurveData && (
            <Tooltip title="下载曲线百分比 CSV">
              <IconButton
                aria-label="下载流量曲线"
                onClick={downloadTrafficCurve}
                size="small"
                sx={{ml: 0.5, p: 0.25}}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            )}
            {hasTrafficCurveData && (
            <Tooltip title="下载今日小时数量 CSV">
              <IconButton
                aria-label="下载今日小时数量"
                onClick={downloadHourlyPlan}
                size="small"
                sx={{ml: 0.5, p: 0.25}}
              >
                <HourlyPlanIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            )}
          </Grid>
        </Grid>
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Postback"/></Grid>
          <Grid item xs>{
            !job.postback ? 'No' :
              (job.postback === 'all' ? 'All' : job.postback.split(',').map(t => postbackTypes[t]).join(', '))
          }</Grid>
        </Grid>
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Device Percent"/></Grid>
          <Grid item xs>{devicePct}</Grid>
        </Grid>
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Landing Rate"/></Grid>
          <Grid item xs>{job.landing}%</Grid>
        </Grid>
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="FreqCtrl ID"/></Grid>
          <Grid item xs>{job.fc_id || job.id}</Grid>
        </Grid>
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Imp FreqCtrl"/></Grid>
          <Grid item xs>
            <Typography variant="span" sx={{mr: 0.8}}>{job.fc_max}</Typography>
            {job.fc_max > 1 && job.prepared > 0 && ext?.pv_percent && (
            <span>({ ext.pv_percent.map((pct, idx) => `${idx+1}: ${pct}%`).join(', ') })</span>
            )}
          </Grid>
        </Grid>
        {job.fc_max > 1 && ext?.pvcnt && (
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="UV Percent"/></Grid>
          <Grid item xs>{formatPercent(ext.pvcnt)}</Grid>
        </Grid>
        )}
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Group ID"/></Grid>
          <Grid item xs>{job.parent || job.id}</Grid>
        </Grid>
        {session.role === 'admin' && (<>
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Organization"/></Grid>
          <Grid item xs>{misc['org'][job.org]}</Grid>
        </Grid>
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Page Actions"/></Grid>
          <Grid item xs>{job.ant_cfg || 'Not Set'}</Grid>
        </Grid>
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Last Updated"/></Grid>
          <Grid item xs>{dayjs(job.updated_at).format('YYYY-MM-DD HH:mm:ss')}</Grid>
        </Grid>
        </>)}
        {job.dest_url.startsWith('adx:') ? (
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Adx TagID"/></Grid>
          <Grid item xs>{job.dest_url.substring(4)}</Grid>
        </Grid>
        ) : (<>
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Landing Url"/></Grid>
          <Grid item xs={9} md={10}><Typography sx={{wordWrap: 'break-word'}}>{job.dest_url || '--'}</Typography></Grid>
        </Grid>
        {job.trackers?.imp && (
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Imp Trackers"/></Grid>
          <Grid item xs={9} md={10}>
            <List dense={true} sx={{p: 0}}>
            {job.trackers.imp.map((tkr, i) =>
              <ListItem key={i} sx={{p: 0}}>
                <ListItemIcon sx={{minWidth: 0}}><LinkIcon /></ListItemIcon>
                <ListItemText sx={{ml: 0, wordWrap: 'break-word'}} primary={tkr} />
              </ListItem>
            )}
            </List>
          </Grid>
        </Grid>
        )}
        {job.trackers?.clk && (
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Click Trackers"/></Grid>
          <Grid item xs={9} md={10}>
            <List dense={true} sx={{p: 0}}>
            {job.trackers.clk.map((tkr, i) =>
              <ListItem key={i} sx={{p: 0}}>
                <ListItemIcon sx={{minWidth: 0}}><LinkIcon /></ListItemIcon>
                <ListItemText sx={{ml: 0, wordWrap: 'break-word'}} primary={tkr} />
              </ListItem>
            )}
            </List>
          </Grid>
        </Grid>
        )}
        </>)}
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Regions"/></Grid>
          <Grid item xs>{((job.attrs&jobAttrExcludeRegion) > 0 ? 'Excl. ' : '') + job.regions}</Grid>
        </Grid>
        {session.role === 'admin' && !!attrs && (
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Attributes"/></Grid>
          <Grid item xs>{attrs}</Grid>
        </Grid>
        )}
        {cust?.cookie && (
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Accept Cookies"/></Grid>
          <Grid item xs>{cust.cookie}%</Grid>
        </Grid>
        )}
        {ext?.osv_percent && (
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="OSV Percent"/></Grid>
          <Grid item xs>{formatOsvPct(ext.osv_percent)}</Grid>
        </Grid>
        )}
        {ext?.ua_tpl && (
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="User Agent"/></Grid>
          <Grid item xs>
            <Typography sx={{whiteSpace: "pre-wrap"}}>{formatUATpl(ext.ua_tpl)}</Typography>
          </Grid>
        </Grid>
        )}
        {job.extra && (
        <Grid item container xs={12}>
          <Grid item xs={3} md={2}><Label name="Extra Info"/></Grid>
          <Grid item xs>
            <Typography sx={{whiteSpace: "pre-wrap"}}>{job.extra.replaceAll('@@', '\n')}</Typography>
          </Grid>
        </Grid>
        )}
      </Grid>
      {trafficChart && (
      <TrafficCurveChart data={trafficCurve} onClose={closeTrafficChart} />
      )}
    </Popup>
  );
};

export default JobState;
